import { openDB, type IDBPDatabase } from 'idb';
import type { CanvasConfig, Project, ProjectListItem } from '../types';
import { generateId } from '../utils/id';

interface DrawlingsDB {
  projects: {
    key: string;
    value: Project;
    indexes: { 'by-updated': number };
  };
}

export class ProjectStorage {
  private db: IDBPDatabase<DrawlingsDB> | null = null;
  private initPromise: Promise<void> | null = null;

  private readonly dbName = 'drawlings-db';
  private readonly dbVersion = 1;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async (): Promise<void> => {
      this.db = await openDB<DrawlingsDB>(this.dbName, this.dbVersion, {
        upgrade(db): void {
          const store = db.createObjectStore('projects', { keyPath: 'id' });
          store.createIndex('by-updated', 'updatedAt');
        },
      });
    })();

    return this.initPromise;
  }

  private ensureDb(): IDBPDatabase<DrawlingsDB> {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  async saveProject(
    id: string | null,
    name: string,
    thumbnail: string,
    config: CanvasConfig,
    drawModeState?: string,
    diagramModeState?: string,
    lastMode?: 'draw' | 'diagram'
  ): Promise<Project> {
    const db = this.ensureDb();
    const now = Date.now();

    const existingProject = id ? await this.getProject(id) : null;

    const project: Project = {
      id: id ?? this.generateProjectId(),
      name,
      createdAt: existingProject?.createdAt ?? now,
      updatedAt: now,

      thumbnail,
      config,
    };

    // Only add state fields if they have values
    const finalDrawState = drawModeState ?? existingProject?.drawModeState;
    const finalDiagramState = diagramModeState ?? existingProject?.diagramModeState;
    if (finalDrawState) {
      project.drawModeState = finalDrawState;
    }
    if (finalDiagramState) {
      project.diagramModeState = finalDiagramState;
    }

    // Save the last active mode
    project.lastMode = lastMode ?? existingProject?.lastMode ?? 'draw';

    await db.put('projects', project);
    return project;
  }

  async getProject(id: string): Promise<Project | undefined> {
    const db = this.ensureDb();
    return db.get('projects', id);
  }

  async listProjects(): Promise<ProjectListItem[]> {
    const db = this.ensureDb();
    const projects = await db.getAllFromIndex('projects', 'by-updated');

    return projects
      .map((p) => ({
        id: p.id,
        name: p.name,
        updatedAt: p.updatedAt,
        thumbnail: p.thumbnail,
      }))
      .reverse();
  }

  async deleteProject(id: string): Promise<void> {
    const db = this.ensureDb();
    await db.delete('projects', id);
  }

  async renameProject(id: string, newName: string): Promise<void> {
    const db = this.ensureDb();
    const project = await this.getProject(id);

    if (project) {
      project.name = newName;
      project.updatedAt = Date.now();
      await db.put('projects', project);
    }
  }

  private generateProjectId(): string {
    return generateId('proj');
  }
}
